"use client";

import Button from "@/components/Button";
import TextField from "@/components/TextField";
import Title from "@/components/Title";
import styles from "./Cadastro.module.scss";
import { useForm, SubmitHandler } from "react-hook-form";
import { useState } from "react";
import useAuth from "@/hooks/useAuth";
import { FormProps } from "@/@types";
import SelectForm from "../SelectForm";
import useData from "@/hooks/useData";
import Link from "next/link";

interface Props {
  user?: any;
  edit?: boolean;
}

export default function Cadastro(props: Props) {
  const { user = null, edit = false } = props;

  const { handleRegister, handleUpdate } = useAuth();
  const { getAddress } = useData();

  const [loading, setLoading] = useState(false);
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormProps>({
    defaultValues: {
      pf_pj: user?.pf_pj || "PF",
      nome: user?.name || "",
      sobrenome: user?.surname || "",
      cpf_cnpj: user?.cpf_cnpj || "",
      telefone: user?.telefone || "",
      endereco: {
        cep: user?.endereco?.cep || "",
        logradouro: user?.endereco?.logradouro || "",
        numero: user?.endereco?.numero || "",
        complemento: user?.endereco?.complemento || "",
        bairro: user?.endereco?.bairro || "",
        cidade: user?.endereco?.cidade || "",
        estado: user?.endereco?.estado || "",
      },
      email: user?.email || "",
      termos: false,
    },
  });

  const submitForm: SubmitHandler<FormProps> = async (data) => {
    setLoading(true);

    const body: FormProps = {
      pf_pj: data.pf_pj,
      nome: data.nome,
      sobrenome: data.sobrenome,
      cpf_cnpj: data.cpf_cnpj,
      telefone: data.telefone,
      endereco: {
        cep: data.endereco.cep,
        logradouro: data.endereco.logradouro,
        numero: data.endereco.numero,
        complemento: data.endereco.complemento,
        bairro: data.endereco.bairro,
        cidade: data.endereco.cidade,
        estado: data.endereco.estado,
      },
      email: data.email,
      password: data.password,
      termos: data.termos,
    };

    if (data.current_password) {
      body.current_password = data.current_password;
      body.new_password = data.new_password;
    }

    if (!edit) {
      await handleRegister(body);
    } else {
      await handleUpdate(body);
    }

    setLoading(false);
  };

  async function handleCepTyping(e: any) {
    if (e.currentTarget.value.replace(/\D/g, "").length === 8) {
      const data = await getAddress(e.currentTarget.value);

      // update react hook form values with data from viacep
      const { bairro, localidade, logradouro, uf } = data;

      // set bairro
      setValue("endereco.bairro", bairro);
      setValue("endereco.cidade", localidade);
      setValue("endereco.logradouro", logradouro);
      setValue("endereco.estado", uf);

      // focus on numero input
      document.getElementById("numero")?.focus();
    }
  }

  return (
    <>
      <div className={styles.cadastro}>
        <div className={styles.radio}>
          <label>
            <input
              type="radio"
              value="PF"
              {...register("pf_pj", { required: true })}
              defaultChecked={user?.pf_pj === "PF" || !user?.pf_pj}
              style={{
                accentColor: "var(--primary)",
              }}
            />
            Pessoa Física
          </label>
          <label>
            <input
              type="radio"
              value="PJ"
              {...register("pf_pj", { required: true })}
              defaultChecked={user?.pf_pj === "PJ"}
              style={{
                accentColor: "var(--primary)",
              }}
            />
            Pessoa Jurídica
          </label>
        </div>

        <div className={`${styles.columns} ${styles.columns_2}`}>
          <TextField
            label="Nome"
            {...register("nome", { required: true })}
            error={errors.nome && "Nome é obrigatório"}
            autoFocus
            defaultValue={watch("nome")}
          />

          <TextField
            label="Sobrenome"
            {...register("sobrenome", { required: true })}
            error={errors.sobrenome && "Sobrenome é obrigatório"}
            defaultValue={watch("sobrenome")}
          />
        </div>
        <div className={`${styles.columns} ${styles.columns_2}`}>
          {/* CPF or CNPJ */}
          {watch("pf_pj") === "PF" && (
            <TextField
              label="CPF"
              mask="999.999.999-99"
              {...register("cpf_cnpj", { required: true })}
              error={errors.cpf_cnpj && "CPF é obrigatório"}
              defaultValue={watch("cpf_cnpj")}
              key="cpf"
            />
          )}
          {watch("pf_pj") === "PJ" && (
            <TextField
              label="CNPJ"
              mask="99.999.999/9999-99"
              {...register("cpf_cnpj", { required: true })}
              error={errors.cpf_cnpj && "CPF é obrigatório"}
              defaultValue={watch("cpf_cnpj")}
              key="cnpj"
            />
          )}

          <TextField
            label="Telefone"
            mask="(99) 99999-9999"
            {...register("telefone", { required: true })}
            error={errors.telefone && "Telefone é obrigatório"}
            defaultValue={watch("telefone")}
          />
        </div>

        <Title
          title="Seus Dados de Endereço"
          subtitle="Informe o endereço para faturamento"
          small
        />

        <div className={`${styles.columns} ${styles.columns_2}`}>
          <TextField
            label="CEP"
            type="text"
            mask="99999-999"
            {...register("endereco.cep", { required: true })}
            error={errors.endereco?.cep && "CEP é obrigatório"}
            onKeyUp={handleCepTyping}
            defaultValue={watch("endereco.cep")}
          />

          <TextField
            label="Logradouro"
            type="text"
            {...register("endereco.logradouro", { required: true })}
            error={errors.endereco?.logradouro && "Logradouro é obrigatório"}
            defaultValue={watch("endereco.logradouro")}
          />

          <TextField
            label="Número"
            type="text"
            {...register("endereco.numero", { required: true })}
            error={errors.endereco?.numero && "Número é obrigatório"}
            id="numero"
            defaultValue={watch("endereco.numero")}
          />
          <TextField
            label="Complemento"
            type="text"
            {...register("endereco.complemento", { required: false })}
            defaultValue={watch("endereco.complemento")}
          />
        </div>

        <div className={`${styles.columns} ${styles.columns_3}`}>
          <TextField
            label="Bairro"
            type="text"
            {...register("endereco.bairro", { required: true })}
            error={errors.endereco?.bairro && "Bairro é obrigatório"}
            defaultValue={watch("endereco.bairro")}
          />
          <TextField
            label="Cidade"
            type="text"
            {...register("endereco.cidade", { required: true })}
            error={errors.endereco?.cidade && "Cidade é obrigatório"}
            defaultValue={watch("endereco.cidade")}
          />

          <SelectForm
            label="Estado"
            options={[
              { label: "São Paulo", value: "SP" },
              { label: "Acre", value: "AC" },
              { label: "Alagoas", value: "AL" },
              { label: "Amapá", value: "AP" },
              { label: "Amazonas", value: "AM" },
              { label: "Bahia", value: "BA" },
              { label: "Ceará", value: "CE" },
              { label: "Distrito Federal", value: "DF" },
              { label: "Espírito Santo", value: "ES" },
              { label: "Goiás", value: "GO" },
              { label: "Maranhão", value: "MA" },
              { label: "Mato Grosso", value: "MT" },
              { label: "Mato Grosso do Sul", value: "MS" },
              { label: "Minas Gerais", value: "MG" },
              { label: "Pará", value: "PA" },
              { label: "Paraíba", value: "PB" },
              { label: "Paraná", value: "PR" },
              { label: "Pernambuco", value: "PE" },
              { label: "Piauí", value: "PI" },
              { label: "Rio de Janeiro", value: "RJ" },
              { label: "Rio Grande do Norte", value: "RN" },
              { label: "Rio Grande do Sul", value: "RS" },
              { label: "Rondônia", value: "RO" },
              { label: "Roraima", value: "RR" },
              { label: "Santa Catarina", value: "SC" },
              { label: "Sergipe", value: "SE" },
              { label: "Tocantins", value: "TO" },
            ]}
            {...register("endereco.estado", { required: true })}
            error={errors.endereco?.estado && "Estado é obrigatório"}
            defaultValue={watch("endereco.estado")}
          />
        </div>
        {!edit && (
          <>
            <Title
              title="Seus Dados de Acesso"
              subtitle="Informe os dados para acesso a plataforma"
              small
            />

            <div className={`${styles.columns} ${styles.columns_2}`}>
              <TextField
                label="Email"
                type="email"
                {...register("email", { required: true })}
                error={errors.email && "Email é obrigatório"}
              />
              <TextField
                label="Senha"
                {...register("password", { required: true })}
                error={errors.password && "Senha é obrigatório"}
                type="password"
              />
            </div>
            <div className={`${styles.columns} ${styles.columns_1}`}>
              <div className={styles.checkbox}>
                <label>
                  <input
                    type="checkbox"
                    {...register("termos", { required: true })}
                  />
                  Eu aceito a{" "}
                  <Link
                    prefetch={false}
                    href="/politica-de-privacidade"
                    target="_blank"
                  >
                    política de privacidade
                  </Link>
                </label>
              </div>
              <small className={styles.error}>
                {errors.termos &&
                  "Você deve aceitar a política de privacidade e os termos de uso"}
              </small>
            </div>
          </>
        )}

        {edit && (
          <>
            <Title
              title="Atualize sua senha"
              subtitle="Deixe em branco caso não queira atualizar sua senha"
              small
            />
            <div className={`${styles.columns} ${styles.columns_2}`}>
              <TextField
                label="Senha atual"
                type="password"
                {...register("current_password", { required: false })}
              />
              <TextField
                label="Nova senha"
                {...register("new_password", { required: false })}
                type="password"
              />
            </div>
          </>
        )}

        <div className={styles.buttons}>
          <Button
            title={edit ? "Atualizar conta" : "Criar conta"}
            onClick={
              // submit form
              handleSubmit((data: any) => submitForm(data))
            }
            loading={loading}
          />
        </div>
      </div>
    </>
  );
}
